import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

/*
 * Allegedly, IntersectionObserver is available as of Safari v12.1.
 * Yet, IntersectionObserver reference errors occur through Safari v14.
 * So, we polyfill it as needed.
 * https://github.com/thebuilder/react-intersection-observer/issues/495
 */
const loadIntersectionObserver = () => import('intersection-observer');
const isIntersectionObserverAvailable = typeof IntersectionObserver !== 'undefined';

export interface IUseIntersectionObserverArgs<T extends HTMLElement> {
  element: T | null;
  observerOptions?: IntersectionObserverInit;
  initialState?: boolean;
}

export function useIntersectionObserver<T extends HTMLElement>({
  element,
  observerOptions,
  initialState = false,
}: IUseIntersectionObserverArgs<T>) {
  const [isReady, setIsReady] = useState<boolean>(isIntersectionObserverAvailable);
  const [isIntersecting, setIsIntersecting] = useState<boolean>(initialState);
  const observerRef = useRef<IntersectionObserver>();

  const observerCallback: IntersectionObserverCallback = useCallback(
    (entries) => {
      const foundElement = entries.find((e) => e.target === element);
      setIsIntersecting(Boolean(foundElement?.isIntersecting));
    },
    [element]
  );

  useEffect(() => {
    if (isIntersectionObserverAvailable) {
      return;
    }

    let mounted = true;

    loadIntersectionObserver()
      .then(() => {
        if (mounted) {
          setIsReady(true);
        }
      })
      .catch(console.error);

    return () => {
      mounted = false;
    };
  }, []);

  useLayoutEffect(() => {
    if (!isReady) {
      return;
    }

    if (!observerRef.current) {
      // TODO: we only need 1 intersection observer per element,
      // so these should live in a context or something instead
      // of being created 1 for each time this hook is used
      observerRef.current = new IntersectionObserver(observerCallback, observerOptions);
    }

    if (element) {
      observerRef.current.observe(element);
    }

    return () => {
      observerRef.current?.disconnect();
      observerRef.current = undefined;
    };
  }, [isReady, element, observerCallback, observerOptions]);

  return { isIntersecting };
}
